import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { Check, X, Eye, GitCompareArrows } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const SCOPE_TEXT = {
  all: 'Sees every record in the company.',
  team: 'Sees only records for the projects they are assigned to.',
  own: 'Sees only their own records.',
};

/** Turn a list of action labels into "view, create and edit". */
const joinWords = (words) => {
  if (words.length <= 1) return words[0] || '';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
};

const roleName = (roles, key) => roles.find((r) => r.role === key)?.label || key;

/**
 * Plain-English summary of what one role can and cannot do — or, in compare
 * mode, exactly where two roles differ. Built from the same catalogue the
 * matrix is drawn from, reading the unsaved draft so it previews edits.
 */
export const RolePermissionPreview = ({ catalog, roles }) => {
  const { t } = useTranslation();
  const [role, setRole] = useState('manager');
  const [compareRole, setCompareRole] = useState('');
  const [comparing, setComparing] = useState(false);

  const actionLabel = (a) => (catalog.action_labels?.[a] || a).toLowerCase();

  const describe = (roleKey) => {
    const selected = roles.find((r) => r.role === roleKey);
    const granted = new Set(selected?.permissions || []);
    const modules = (catalog.modules || []).map((m) => {
      const allowed = m.actions.filter((a) => granted.has(`${m.key}.${a}`));
      const denied = m.actions.filter((a) => !granted.has(`${m.key}.${a}`));
      return {
        key: m.key,
        label: m.label,
        allowed,
        denied,
        sentence: allowed.length === 0
          ? `No access to ${m.label}.`
          : `Can ${joinWords(allowed.map(actionLabel))}.`,
        deniedSentence: denied.length && allowed.length
          ? `Cannot ${joinWords(denied.map(actionLabel))}.`
          : '',
      };
    });
    return { scope: selected?.data_scope, modules };
  };

  const left = describe(role);
  const right = comparing && compareRole ? describe(compareRole) : null;

  const canDo = left.modules.filter((m) => m.allowed.length > 0);
  const cannotDo = left.modules.filter((m) => m.allowed.length === 0);

  // Compare mode: per module, which actions only one side has.
  const diff = (() => {
    if (!right) return [];
    return left.modules.map((m, i) => {
      const other = right.modules[i];
      const onlyLeft = m.allowed.filter((a) => !other.allowed.includes(a));
      const onlyRight = other.allowed.filter((a) => !m.allowed.includes(a));
      return { key: m.key, label: m.label, onlyLeft, onlyRight };
    }).filter((d) => d.onlyLeft.length || d.onlyRight.length);
  })();

  const sameScope = right && (left.scope || 'all') === (right.scope || 'all');

  return (
    <Card className="mt-6" data-testid="role-permission-preview">
      <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
        <CardTitle className="text-base flex items-center gap-2">
          <Eye className="h-4 w-4" />
          {comparing ? t('auto.misc2.whereTheseRolesDiffer') : t('auto.misc2.whatThisRoleCanDo')}
        </CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-[180px]" data-testid="preview-role-select">
              <SelectValue placeholder={t('auto.misc2.pickARole')} />
            </SelectTrigger>
            <SelectContent>
              {roles.map((r) => (
                <SelectItem key={r.role} value={r.role}>{r.label || r.role}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {comparing && (
            <>
              <span className="text-xs text-slate-400">vs</span>
              <Select value={compareRole} onValueChange={setCompareRole}>
                <SelectTrigger className="w-[180px]" data-testid="compare-role-select">
                  <SelectValue placeholder={t('auto.misc2.compareWith')} />
                </SelectTrigger>
                <SelectContent>
                  {roles.filter((r) => r.role !== role).map((r) => (
                    <SelectItem key={r.role} value={r.role}>{r.label || r.role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          <Button
            variant={comparing ? 'secondary' : 'ghost'}
            size="sm"
            className="text-xs h-9 gap-1"
            onClick={() => {
              setComparing((v) => !v);
              if (!compareRole) {
                setCompareRole(roles.find((r) => r.role !== role)?.role || '');
              }
            }}
            data-testid="compare-roles-toggle"
          >
            <GitCompareArrows className="h-3.5 w-3.5" />
            {comparing ? t('auto.misc2.singleRole') : t('auto.misc2.compareRoles')}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!comparing && (
          <>
            <div className="text-sm text-slate-600" data-testid="preview-scope">
              <Badge variant="outline" className="mr-2">{t('auto.misc2.dataScope')}</Badge>
              {SCOPE_TEXT[left.scope] || SCOPE_TEXT.all}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <div className="text-xs font-semibold text-emerald-700 mb-2 flex items-center gap-1">
                  <Check className="h-3.5 w-3.5" /> Allowed ({canDo.length})
                </div>
                <ul className="space-y-1.5" data-testid="preview-allowed">
                  {canDo.map((m) => (
                    <li key={m.key} className="text-xs text-slate-700">
                      <span className="font-medium">{m.label}</span> — {m.sentence}
                      {m.deniedSentence && (
                        <span className="text-slate-400"> {m.deniedSentence}</span>
                      )}
                    </li>
                  ))}
                  {canDo.length === 0 && <li className="text-xs text-slate-400">{t('auto.misc2.nothingGrantedYet')}</li>}
                </ul>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1">
                  <X className="h-3.5 w-3.5" /> No access ({cannotDo.length})
                </div>
                <ul className="space-y-1.5" data-testid="preview-denied">
                  {cannotDo.map((m) => (
                    <li key={m.key} className="text-xs text-slate-500">{m.label}</li>
                  ))}
                  {cannotDo.length === 0 && <li className="text-xs text-slate-400">{t('auto.misc2.fullAccess')}</li>}
                </ul>
              </div>
            </div>
          </>
        )}

        {comparing && !compareRole && (
          <p className="text-sm text-slate-500">{t('auto.misc2.pickASecondRoleToCompare')}</p>
        )}

        {comparing && compareRole && (
          <>
            <div className="text-sm text-slate-600" data-testid="compare-scope">
              <Badge variant="outline" className="mr-2">{t('auto.misc2.dataScope')}</Badge>
              {sameScope ? (
                <>Both roles: {(SCOPE_TEXT[left.scope] || SCOPE_TEXT.all).toLowerCase()}</>
              ) : (
                <>
                  <span className="font-medium">{roleName(roles, role)}</span>{' '}
                  {(SCOPE_TEXT[left.scope] || SCOPE_TEXT.all).toLowerCase()}{' '}
                  <span className="font-medium">{roleName(roles, compareRole)}</span>{' '}
                  {(SCOPE_TEXT[right.scope] || SCOPE_TEXT.all).toLowerCase()}
                </>
              )}
            </div>

            {diff.length === 0 ? (
              <p className="text-sm text-emerald-700" data-testid="compare-identical">
                {t('auto.misc2.theseTwoRolesCanDoExactly')}
              </p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <div className="text-xs font-semibold text-emerald-700 mb-2">
                    Only {roleName(roles, role)} can
                  </div>
                  <ul className="space-y-1.5" data-testid="compare-only-left">
                    {diff.filter((d) => d.onlyLeft.length).map((d) => (
                      <li key={d.key} className="text-xs text-slate-700">
                        <span className="font-medium">{d.label}</span> —{' '}
                        {joinWords(d.onlyLeft.map(actionLabel))}
                      </li>
                    ))}
                    {diff.every((d) => !d.onlyLeft.length) && (
                      <li className="text-xs text-slate-400">{t('auto.misc2.nothingExtra')}</li>
                    )}
                  </ul>
                </div>
                <div>
                  <div className="text-xs font-semibold text-indigo-700 mb-2">
                    Only {roleName(roles, compareRole)} can
                  </div>
                  <ul className="space-y-1.5" data-testid="compare-only-right">
                    {diff.filter((d) => d.onlyRight.length).map((d) => (
                      <li key={d.key} className="text-xs text-slate-700">
                        <span className="font-medium">{d.label}</span> —{' '}
                        {joinWords(d.onlyRight.map(actionLabel))}
                      </li>
                    ))}
                    {diff.every((d) => !d.onlyRight.length) && (
                      <li className="text-xs text-slate-400">{t('auto.misc2.nothingExtra')}</li>
                    )}
                  </ul>
                </div>
              </div>
            )}
          </>
        )}

        {catalog.admin_only?.length > 0 && (
          <p className="text-[11px] text-slate-500">
            Plus {catalog.admin_only.length} administrator-only actions (backups, user
            management, integrations) that no other role can be given.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default RolePermissionPreview;
